/* eslint-disable no-restricted-syntax */
/* eslint-disable class-methods-use-this */
const convertWeight = require('../../helpers/weightConverter');
const convertVolume = require('../../helpers/volumeConverter');
const { freightUnit } = require('../../constants/payments');
const { orderType: OrderType, movementType, sourceChargeCode, getVehicleTypes } = require('pando-constants');

const { PTL } = getVehicleTypes();
const Helper = require('./utils/helper');

const CONVERTERS = {
  [freightUnit.per_ton]: m => convertWeight(m.weightUnit, 't', m.weight),
  [freightUnit.per_kg]: m => convertWeight(m.weightUnit, 'kg', m.weight),
  [freightUnit.per_cft]: m => convertVolume(m.volumeUnit, 'cft', m.volume),
};

const PACKAGE_UNITS = [freightUnit.per_package, freightUnit.per_case, freightUnit.per_box];

class Loading {
  calaculateQuantity(
    materialList, unitType, materialMap, quantityUnitsForPieces, sourceChargeMaster = false,
  ) {
    let result = 0;
    if (sourceChargeMaster && unitType === freightUnit.per_trip) {
      result = 1;
    } else if (!materialList || !materialList.length) {
      result = 0;
    } else {
      const converter = CONVERTERS[unitType];
      if (converter) {
        result = materialList.reduce((acc, m) => acc + converter(m), 0);
      } else if (PACKAGE_UNITS.includes(unitType)) {
        result = materialList.reduce((acc, m) => {
          const qty = Math.ceil(m.quantity || 0);
          const quantityUnit = m.quantityUnit && m.quantityUnit.toLowerCase();
          if (quantityUnit && quantityUnitsForPieces[quantityUnit]) {
            const detail = materialMap[m.materialCode];
            return detail
              && detail.pce_box ? acc + Math.ceil(qty / Math.ceil(detail.pce_box)) : acc;
          }
          return acc + qty;
        }, 0);
      }
    }
    return result;
  }

  createDestinationMap(destinations) {
    return destinations.reduce((map, dest) => {
      const sourceId = dest.sourceLocationId.toString();
      (map[sourceId] = map[sourceId] || []).push(dest);
      return map;
    }, {});
  }

  calculateChargeAmount(chargeItem, quantity) {
    const { charge = 0, min_charge = 0, charge_unit: chargeUnit } = chargeItem;
    const calculatedCharge = chargeUnit === freightUnit.per_trip ? charge : charge * quantity;
    return Math.max(calculatedCharge, min_charge);
  }

  getUniqueDestinationPairs(indentDestinations) {
    const uniquePairs = new Set();
    indentDestinations.forEach((item) => {
      const locationId = item.location_id != null ? item.location_id.toString() : '';
      const gateId = item.gate_id != null ? item.gate_id.toString() : '';
      uniquePairs.add(`${locationId}_${gateId}`);
    });
    return uniquePairs.size;
  }

  createSourceHash(sourceLocationId, sourceGateId, sourceId, gateId) {
    let targetHash = sourceId ? sourceId.toString() : '';
    let hash = sourceLocationId ? sourceLocationId.toString() : '';
    if (sourceGateId) {
      targetHash += `-${gateId ? gateId.toString() : ''}`;
      hash += `-${sourceGateId.toString()}`;
    }
    return { targetHash, hash };
  }

  filterMatchingMaterials(normalizedMaterials, sourceId, gateId, dest) {
    return normalizedMaterials.filter((material) => {
      const materialSourceLocationId = dest.sourceLocationId ? dest.sourceLocationId.toString() : '';
      const materialSourceGateId = material.source_gate_id ? material.source_gate_id.toString() : '';
      return `${materialSourceLocationId}${materialSourceGateId}` === `${sourceId}${gateId}`;
    });
  }

  findSourceChargesForMaterial(material, dest, sourceCharges) {
    const materialGateId = material.source_gate_id ? material.source_gate_id.toString() : '';
    const destLocationId = dest.sourceLocationId ? dest.sourceLocationId.toString() : '';

    return sourceCharges.filter((source) => {
      const sourceLocId = source.source_location_id ? source.source_location_id.toString() : '';
      const sourceGateId = source.source_gate_id ? source.source_gate_id.toString() : '';
      return source.charge_code === sourceChargeCode.LOADING
        && sourceLocId + sourceGateId === destLocationId + materialGateId;
    });
  }

  calculateMpsdChargeValue(
    matchingMaterials, dest, sourceCharges, materialMap, quantityUnitsForPieces, chargeItem,
  ) {
    let chargeValue = 0;
    matchingMaterials.forEach((material) => {
      const filterSourceCharges = this.findSourceChargesForMaterial(material, dest, sourceCharges);
      if (filterSourceCharges.length > 0) {
        filterSourceCharges.forEach((sourceCharge) => {
          sourceCharge.charges.forEach((_charge) => {
            const quantity = this.calaculateQuantity(matchingMaterials, _charge.charge_unit, materialMap, quantityUnitsForPieces, true);
            chargeValue = this.calculateChargeAmount(chargeItem, quantity);
          });
        });
      }
    });
    return chargeValue;
  }

  processMpsdDestinations(
    destinationsBySource, sourceId, gateId, sourceCharges, materialMap, quantityUnitsForPieces,
    chargeItem,
  ) {
    return destinationsBySource.reduce((acc, dest) => {
      const normalizedMaterials = Helper.getNormalizedMaterialDetails(dest.materials);
      const matchingMaterials = this.filterMatchingMaterials(
        normalizedMaterials, sourceId, gateId, dest,
      );

      if (matchingMaterials.length > 0) {
        const chargeValue = this.calculateMpsdChargeValue(
          matchingMaterials, dest, sourceCharges, materialMap, quantityUnitsForPieces, chargeItem,
        );
        return acc + chargeValue;
      }
      return acc;
    }, 0);
  }

  processRegularDestinations(
    destinationsBySource, gateId, chargeItem, materialMap, quantityUnitsForPieces,
  ) {
    const totalQuantity = destinationsBySource.reduce((acc, dest) => {
      if (dest.sourceGateId && dest.sourceGateId.equals(gateId)) {
        const normalizedMaterials = Helper.getNormalizedMaterialDetails(dest.materials);
        const quantity = this.calaculateQuantity(
          normalizedMaterials,
          chargeItem.charge_unit,
          materialMap,
          quantityUnitsForPieces,
          true,
        );
        return acc + quantity;
      }
      return acc;
    }, 0);
    return this.calculateChargeAmount(chargeItem, totalQuantity);
  }

  processChargeItem(
    chargeItem, sourceId, destinationsBySourceMap,
    materialMap, quantityUnitsForPieces, gateId, isMpsd = false,
    sourceCharges = [],
  ) {
    let result;

    if (chargeItem.charge_unit === freightUnit.per_trip) {
      result = this.calculateChargeAmount(chargeItem, 1);
    } else {
      const destinationsBySource = destinationsBySourceMap[sourceId] || [];

      if (isMpsd) {
        result = this.processMpsdDestinations(
          destinationsBySource, sourceId, gateId, sourceCharges,
          materialMap, quantityUnitsForPieces, chargeItem,
        );
      } else {
        result = this.processRegularDestinations(
          destinationsBySource, gateId, chargeItem,
          materialMap, quantityUnitsForPieces,
        );
      }
    }
    return result;
  }

  getSourceChargeConfig(sources, data, deliveryType, idMap) {
    const destinationLength = this.getUniqueDestinationPairs(data.indentDestinationsFromOrder);
    const isMpmdPtl = sources.length > 1 && destinationLength > 1 && deliveryType === PTL;
    const isMpsd = sources.length > 1 && destinationLength === 1;
    const sourcesToPick = isMpmdPtl ? (data.sources || []) : sources;
    const sourceGateId = isMpmdPtl ? (idMap.gateId || null) : null;
    return { isMpmdPtl, isMpsd, sourcesToPick, sourceGateId };
  }

  filterLoadingCharges(data, sourceId, gateId) {
    return (data.indentSourceCharges || []).filter((c) => {
      if (c.charge_code !== sourceChargeCode.LOADING) {
        return false;
      }
      if (
        data.clientConfiguration.source_charges
        && data.clientConfiguration.source_charges.loading
        && ['city', 'state', 'region', 'point'].includes(data.clientConfiguration.source_charges.loading.toLowerCase())
      ) {
        const type = data.clientConfiguration.source_charges.loading.toLowerCase();
        const { sourceGateDetailsMap } = data;
        return sourceGateDetailsMap[`${sourceId}-${gateId}`][type] === c[type];
      }
      const {
        targetHash, hash,
      } = this.createSourceHash(c.source_location_id, c.source_gate_id, sourceId, gateId);
      return hash === targetHash;
    });
  }

  processSourceCharges(
    sources = [],
    destinationsBySourceMap,
    data, materialMap, quantityUnitsForPieces, deliveryType = null, idMap = {},
  ) {
    let result = 0;

    if (data.indentSourceCharges && data.indentSourceCharges.length > 0) {
      const {
        isMpsd, sourcesToPick, sourceGateId,
      } = this.getSourceChargeConfig(sources, data, deliveryType, idMap);

      result = sourcesToPick.reduce((total, source) => {
        const sourceId = source.locationId
          ? source.locationId.toString() : source.location_id.toString();
        const gateId = sourceGateId || (source.gateId
          ? source.gateId.toString() : source.gate_id.toString());
        const loadingCharges = this.filterLoadingCharges(data, sourceId, gateId);

        return total + loadingCharges.reduce((chargeTotal, sc) => chargeTotal + (sc.charges || [])
          .reduce((itemTotal, chargeItem) => itemTotal + this.processChargeItem(
            chargeItem,
            sourceId,
            destinationsBySourceMap,
            materialMap,
            quantityUnitsForPieces,
            gateId,
            isMpsd,
            data.indentSourceCharges,
          ), 0), 0);
      }, 0);
    }
    return result;
  }

  getDepotId(isInwardOrReturns, masterDepot, sourceId, indentDestinations, sourceLocationId) {
    let result;

    if (isInwardOrReturns) {
      const foundDestination = indentDestinations
        .find(d => d.sourceLocationId.toString() === sourceId);
      result = masterDepot || (foundDestination && foundDestination.locationId);
    } else {
      result = masterDepot || sourceLocationId;
    }
    return result;
  }

  processDepotCharges(
    sources, destinationsBySourceMap, data,
    materialMap, quantityUnitsForPieces, isInwardOrReturns, masterDepot,
  ) {
    const result = sources.reduce((total, source) => {
      const sourceId = source.locationId.toString();
      const destinationsBySource = destinationsBySourceMap[sourceId] || [];
      const depotId = this.getDepotId(
        isInwardOrReturns, masterDepot, sourceId, data.indentDestinations, source.locationId,
      );
      const loadingCostMaster = data.loadingChargesMaster && data.loadingChargesMaster[depotId];
      let sourceTotal = total;

      if (loadingCostMaster) {
        const loadingCost = loadingCostMaster.loadingCost || 0;
        if (loadingCostMaster.unit === freightUnit.per_trip) {
          sourceTotal = total + loadingCost;
        } else {
          const quantityTotal = destinationsBySource
            .reduce((acc, destination) => acc + this.calaculateQuantity(
              Helper.getNormalizedMaterialDetails(destination.materials),
              loadingCostMaster.unit,
              materialMap,
              quantityUnitsForPieces,
            ), 0);
          sourceTotal = total + (quantityTotal * loadingCost);
        }
      }
      return sourceTotal;
    }, 0);
    return result;
  }

  loadingWithSourceDepots(data, depotConfiguration, idMap = {}) {
    let result = 0;
    if (!(data.orderType === OrderType.OPEN
      && !(depotConfiguration && depotConfiguration.open_indents_charge
        && depotConfiguration.open_indents_charge.loading_charge))) {
      const quantityUnitsForPieces = Helper.getQuantityUnitsForPieces(data.customQuantityUnits);
      const materialMap = Helper.fetchMaterialMap(data.materials.masterData);
      const isInwardOrReturns = data.movement === movementType.INWARD
        || data.movement === movementType.RETURNS;
      const masterDepot = data.routeDetails && data.routeDetails.depot_id;
      const destinationsBySourceMap = this.createDestinationMap(data.destinations);
      let gateCharges = 0;

      const depotCharges = this.processDepotCharges(
        data.sources,
        destinationsBySourceMap,
        data,
        materialMap,
        quantityUnitsForPieces,
        isInwardOrReturns,
        masterDepot,
      );
      logger.info(`[LoadingCharge]: ${data.orderNo} - Depot charge = ${depotCharges}`);
      if (data.clientConfiguration && data.clientConfiguration.source_charges
        && data.clientConfiguration.source_charges.loading) {
        gateCharges = this.processSourceCharges(
          data.indentSourcesFromOrder,
          destinationsBySourceMap,
          data,
          materialMap,
          quantityUnitsForPieces,
          data.deliveryType,
          idMap,
        );
        logger.info(`[LoadingCharge]: ${data.orderNo} - Gate charge = ${gateCharges}`);
      }
      result = depotCharges + gateCharges;
    }
    logger.info(`[LoadingCharge]: ${data.orderNo} - Total = ${result}`);
    return result;
  }
}

module.exports = Loading;
